import { ref, computed } from 'vue';

/**
 * MotorPlan Payments Theme Configuration
 *
 * @typedef {Object} ThemeConfig
 * @property {string} [primary] - RGB triplet e.g. "0 178 168"
 * @property {string} [accent] - RGB triplet e.g. "216 217 219"
 * @property {string} [navy] - RGB triplet e.g. "16 24 32"
 * @property {string} [bg] - Page background
 * @property {string} [surface] - Card backgrounds
 * @property {string} ['surface-2'] - Nested surfaces
 * @property {string} [text] - Primary text
 * @property {string} ['text-muted'] - Secondary text
 * @property {string} [border] - Borders
 * @property {string} [ring] - Focus rings
 */

const THEME_STORAGE_KEY = 'arg-theme-mode';
const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)';

// Shared reactive state (singleton pattern for app-wide theme)
const isDarkMode = ref(false);
const currentThemeOverrides = ref(null);

/**
 * Theme composable for MotorPlan Payments brand theming system.
 *
 * Features:
 * - Dark/light mode toggle with localStorage persistence
 * - System preference detection
 * - Runtime theme overrides for white-labeling
 *
 * @returns {Object} Theme utilities
 */
export function useTheme() {
  // Computed for theme mode
  const themeMode = computed(() => (isDarkMode.value ? 'dark' : 'light'));

  /**
   * Initialize theme from localStorage or system preference.
   *
   * IMPORTANT: This function should only be called ONCE on app mount (in App.vue).
   * The media query listener is intentionally not cleaned up because:
   * 1. This is a singleton pattern - the composable uses module-scoped state
   * 2. The listener should persist for the entire app lifecycle
   * 3. The app is a SPA, so the listener is only added once per page load
   */
  function initializeTheme() {
    // Check localStorage first
    const stored = localStorage.getItem(THEME_STORAGE_KEY);

    if (stored === 'dark') {
      isDarkMode.value = true;
    } else if (stored === 'light') {
      isDarkMode.value = false;
    } else {
      // Fall back to system preference
      isDarkMode.value = window.matchMedia(SYSTEM_DARK_QUERY).matches;
    }

    // Apply to document
    applyDarkModeClass();

    // Listen for system preference changes
    const mediaQuery = window.matchMedia(SYSTEM_DARK_QUERY);
    mediaQuery.addEventListener('change', (e) => {
      // Only auto-switch if user hasn't set a preference
      if (!localStorage.getItem(THEME_STORAGE_KEY)) {
        isDarkMode.value = e.matches;
        applyDarkModeClass();
      }
    });
  }

  /**
   * Apply or remove the 'dark' class on the document element.
   */
  function applyDarkModeClass() {
    if (isDarkMode.value) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }

  /**
   * Toggle between dark and light mode.
   */
  function toggleDarkMode() {
    isDarkMode.value = !isDarkMode.value;
    localStorage.setItem(THEME_STORAGE_KEY, isDarkMode.value ? 'dark' : 'light');
    applyDarkModeClass();
  }

  /**
   * Set dark mode explicitly.
   *
   * @param {boolean} enabled - Whether dark mode should be enabled
   */
  function setDarkMode(enabled) {
    isDarkMode.value = enabled;
    localStorage.setItem(THEME_STORAGE_KEY, enabled ? 'dark' : 'light');
    applyDarkModeClass();
  }

  /**
   * Use system preference for theme (clears user preference).
   */
  function useSystemPreference() {
    localStorage.removeItem(THEME_STORAGE_KEY);
    isDarkMode.value = window.matchMedia(SYSTEM_DARK_QUERY).matches;
    applyDarkModeClass();
  }

  /**
   * Apply custom theme overrides for white-labeling.
   * These override the default MotorPlan Payments colors.
   *
   * @param {ThemeConfig|null} config - Theme configuration to apply, or null to reset
   */
  function applyTheme(config) {
    const root = document.documentElement;

    if (!config) {
      // Reset to defaults by removing custom properties
      currentThemeOverrides.value = null;
      const overridableKeys = [
        'primary',
        'accent',
        'navy',
        'bg',
        'surface',
        'surface-2',
        'text',
        'text-muted',
        'border',
        'ring',
        'success',
        'warning',
        'error',
        'info',
      ];
      overridableKeys.forEach((key) => {
        root.style.removeProperty(`--brand-${key}`);
      });
      return;
    }

    currentThemeOverrides.value = config;

    // Apply each provided override
    Object.entries(config).forEach(([key, value]) => {
      if (value) {
        root.style.setProperty(`--brand-${key}`, value);
      }
    });
  }

  /**
   * Load and apply theme from merchant settings.
   *
   * @param {Object|null} merchantSettings - Merchant settings object containing theme config
   */
  function loadMerchantTheme(merchantSettings) {
    if (!merchantSettings?.theme) {
      // No custom theme, reset to defaults
      applyTheme(null);
      return;
    }

    applyTheme(merchantSettings.theme);
  }

  /**
   * Convert hex color to RGB triplet.
   *
   * @param {string} hex - Hex color (e.g., "#00b2a8" or "00b2a8")
   * @returns {string} RGB triplet (e.g., "0 178 168"), or empty string if invalid
   */
  function hexToRgbTriplet(hex) {
    if (typeof hex !== 'string') {
      return '';
    }

    // Normalize input: trim whitespace and remove leading '#' if present
    const normalized = hex.trim().replace(/^#/, '');

    // Validate that we have exactly 6 hex digits
    if (!/^[0-9a-fA-F]{6}$/.test(normalized)) {
      return '';
    }

    const r = parseInt(normalized.substring(0, 2), 16);
    const g = parseInt(normalized.substring(2, 4), 16);
    const b = parseInt(normalized.substring(4, 6), 16);

    return `${r} ${g} ${b}`;
  }

  return {
    // State
    isDarkMode: computed(() => isDarkMode.value),
    themeMode,
    currentThemeOverrides: computed(() => currentThemeOverrides.value),

    // Actions
    initializeTheme,
    toggleDarkMode,
    setDarkMode,
    useSystemPreference,
    applyTheme,
    loadMerchantTheme,

    // Utilities
    hexToRgbTriplet,
  };
}
